import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiIcon } from '@taiga-ui/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { SlTpControlsComponent } from '@shared/components/trading/orders/common/sl-tp-controls/sl-tp-controls.component'
import { SlTpEnum } from '@modules/stocks-module/orders/enums/sl-tp.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { finalize, Subject, takeUntil } from 'rxjs'
import { SlTpInterface } from '@modules/stocks-module/orders/interfaces/sl-tp.interface'
import { CreateOrderFromPositionInterface } from '@modules/stocks-module/orders/interfaces/create-order-from-position.interface'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { SlTpType } from '@shared/components/trading/orders/common/sl-tp-controls/core/types/sl-tp.type'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { QuantitySelectorComponent } from '@shared/components/trading/positions/stocks/common/quantity-selector/quantity-selector.component'
import { AlertService } from '@shared/services/alert.service'
import { PositionsStocksService } from '@modules/stocks-module/positions/services/positions-stocks.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { TrailingStopControlsComponent } from '@shared/components/trading/orders/common/trailing-stop-controls/trailing-stop-controls.component'
import { TrailInterface } from '@modules/stocks-module/orders/interfaces/trail.interface'
import { TuiSwitch } from '@taiga-ui/kit'

@UseLocales({ ru, en })
@Component({
	selector: 'app-create-oco-order',
	templateUrl: './create-oco-order.component.html',
	styleUrl: './create-oco-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TuiSwitch,
		FormsModule,
		TranslatePipe,
		ReactiveFormsModule,
		SlTpControlsComponent,
		QuantitySelectorComponent,
		ButtonComponent,
		TrailingStopControlsComponent,
	],
})
export class CreateOcoOrderComponent implements OnInit, OnDestroy {
	protected readonly slTpEnum: typeof SlTpEnum = SlTpEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum

	public position: InputSignal<PositionsInterface | null> = input<PositionsInterface | null>(null)

	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _positionsStocksService: PositionsStocksService =
		inject<PositionsStocksService>(PositionsStocksService)
	private readonly _stocksOrdersService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)

	protected resQty: WritableSignal<number> = signal(0)
	protected isLoadingOrders: WritableSignal<boolean> = signal(false)
	protected isLoadingCreateOCO: WritableSignal<boolean> = signal(false)
	protected defaultPercentTakeProfit: WritableSignal<number> = signal(1)
	protected isTrailingStopActive: WritableSignal<boolean> = signal<boolean>(false)
	protected trail: WritableSignal<TrailInterface> = signal<TrailInterface>({ trail_price: null, trail_percent: null })
	protected slTpPrices: WritableSignal<SlTpInterface<number>> = signal<SlTpInterface<number>>({
		stopLoss: 0,
		takeProfit: 0,
	})

	protected isSlTpActive: WritableSignal<SlTpInterface<boolean>> = signal({
		stopLoss: true,
		takeProfit: true,
	})

	protected tickerPrice: Signal<number> = computed<number>((): number => +(this.position()?.current_price ?? 0))

	protected isInvalidOrder: Signal<boolean> = computed<boolean>((): boolean => {
		const position: PositionsInterface | null = this.position()
		if (!position) return true

		if (this.isTrailingStopActive()) {
			return !this.trail().trail_price && !this.trail().trail_percent
		}

		const isSlTpActive: SlTpInterface<boolean> = this.isSlTpActive()
		const slTpPrices: SlTpInterface<number> = this.slTpPrices()

		switch (position.side) {
			case PositionSideEnum.LONG:
				return (
					(isSlTpActive.stopLoss && slTpPrices.stopLoss >= +position.current_price) ||
					(isSlTpActive.takeProfit && slTpPrices.takeProfit <= +position.current_price)
				)
			case PositionSideEnum.SHORT:
				return (
					(isSlTpActive.stopLoss && slTpPrices.stopLoss <= +position.current_price) ||
					(isSlTpActive.takeProfit && slTpPrices.takeProfit >= +position.current_price)
				)
			default:
				return false
		}
	})
	private _listenLoadingOpenedOrders(): void {
		this._ordersStocksHelperService.ordersLoadingMap
			.get(this.orderStateEnum.OPEN)
			?.pipe(takeUntil(this._destroy$))
			.subscribe((loading: boolean): void => {
				this.isLoadingOrders.set(loading)
			})
	}

	private _setDefaultPercentTakeProfit(): void {
		const position: PositionsInterface | null = this.position()

		if (!this.position()) {
			this.defaultPercentTakeProfit.set(1)
			return
		}
		if (!position) return

		const { market_value, cost_basis, avg_entry_price, qty, side }: PositionsInterface = position
		const difference: number = (+market_value - +cost_basis) / (+qty * +avg_entry_price)

		this.defaultPercentTakeProfit.set(
			side === this.positionSideEnum.SHORT ? difference * -100 + 1 : difference * 100 + 1,
		)
	}

	private _generateOcoOrder(): CreateOrderFromPositionInterface | null {
		const position: PositionsInterface | null = this.position()
		if (!position) return null

		if (this.isInvalidOrder()) return null

		const { symbol, side }: PositionsInterface = position
		const { stopLoss, takeProfit }: SlTpInterface<number> = this.slTpPrices()
		const isSlTpActive: SlTpInterface<boolean> = this.isSlTpActive()

		const resSide: OrderSideEnum =
			side === this.positionSideEnum.LONG ? this.orderSideEnum.SELL : this.orderSideEnum.BUY

		return {
			symbol,
			qty: this.resQty(),
			side: resSide,
			...(isSlTpActive.stopLoss && { stop_loss: { stop_price: stopLoss.toString() } }),
			...(isSlTpActive.takeProfit && {
				take_profit: { limit_price: takeProfit.toString() },
			}),
		}
	}

	private _generateTrailingStopOrder(): CreateOrderInterface | null {
		const position: PositionsInterface | null = this.position()
		const { trail_price, trail_percent }: TrailInterface = this.trail()
		if (!position) return null
		if (!trail_price && !trail_percent) return null

		const resSide: OrderSideEnum =
			position.side === this.positionSideEnum.LONG ? this.orderSideEnum.SELL : this.orderSideEnum.BUY

		return {
			symbol: position.symbol,
			qty: this.resQty(),
			side: resSide,
			type: this.orderTypeEnum.TRAILING_STOP,
			time_in_force: this.orderTermEnum.GTC,
			extended_hours: false,
			...(trail_price && { trail_price }),
			...(trail_percent && { trail_percent }),
		}
	}

	protected onChangeQty(qty: number): void {
		this.resQty.set(qty)
	}

	protected onChangeTrail(trail: TrailInterface): void {
		this.trail.set(trail)
	}

	protected toggleTrailingStop(value: boolean): void {
		this.isTrailingStopActive.set(value)
		if (value) {
			this.isSlTpActive.set({ stopLoss: false, takeProfit: false })
		} else {
			this.isSlTpActive.set({ stopLoss: true, takeProfit: true })
		}
	}

	protected createTrailingStopOrder(): void {
		const order: CreateOrderInterface | null = this._generateTrailingStopOrder()
		if (!order) return

		this.isLoadingCreateOCO.set(true)
		this._stocksOrdersService
			.createOrder(order)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingCreateOCO.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('slTp.positionsSuccessCreated'))
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('slTp.cantCreatePosition'),
					)
				},
			)
	}

	protected createOcoOrder(): void {
		const resOrder: CreateOrderFromPositionInterface | null = this._generateOcoOrder()
		if (!resOrder) return

		this.isLoadingCreateOCO.set(true)
		this._positionsStocksService
			.createOrderFromPosition(resOrder)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingCreateOCO.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('slTp.positionsSuccessCreated'))
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('slTp.cantCreatePosition'),
					)
				},
			)
	}

	protected toggleActiveSlTp(type: SlTpType, value: boolean): void {
		if (value) this.isTrailingStopActive.set(false)
		this.isSlTpActive.set({
			...this.isSlTpActive(),
			[type === 'stop-loss' ? this.slTpEnum.STOP_LOSS : this.slTpEnum.TAKE_PROFIT]: value,
		})
	}

	protected togglePriceSlTp(type: SlTpType, value: number): void {
		const slTpPrices: SlTpInterface<number> = this.slTpPrices()
		this.slTpPrices.set({
			...slTpPrices,
			[type === 'stop-loss' ? this.slTpEnum.STOP_LOSS : this.slTpEnum.TAKE_PROFIT]: value,
		})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._listenLoadingOpenedOrders()
		this._setDefaultPercentTakeProfit()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
